/**
 * @file backlightDomain.ts
 * @description GCOME_AP 背光灯效领域描述（Static / Dynamic 共用功能变量区背光字段）。
 *
 * GCOME_AP 的背光配置存放在功能变量区（GetFunc/SetFunc，CMD=0x05/0x06）：
 *   偏移 8   — 背光模式（文档枚举 0-22，0xff=关闭）
 *   偏移 9   — 亮度
 *   偏移 10  — 速度
 *   偏移 11  — 方向
 *   偏移 12  — 彩色标志
 *   偏移 13  — 单色索引
 *   偏移 14-16 — RGB 主色
 * 本领域 parser 以"偏移 8 起"的 9 字节子数组作为 buffer。
 */

import {
  LightingDomainDescriptor,
  LightingDomainType,
  LightingFeatureFlag,
  LightingConfigData,
} from '../../../../types/lightingDomain'

/** 背光配置在功能变量区的起始偏移。 */
const BACKLIGHT_BASE = 8

/** 背光配置占用字节数（偏移 8-16）。 */
const BACKLIGHT_SIZE = 9

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value))

/**
 * 领域配置中 GCOME_AP 独有的扩展字段（模式/方向/单色索引），供 setProperty 合并与序列化。
 * 注意 lightMode/sideMode 必须被 parse 读回并在 serialize 写回 buffer[0]，
 * 否则 shared 的读-改-写（setConfig/setProperty）会把灯效模式覆盖成 0（GAME_MODE 自定义）。
 */
export type BacklightConfigExtra = {
  lightMode?: number
  direction?: boolean
  colorIndex?: number
}

const backlightParser: LightingDomainDescriptor['parser'] = {
  parse: (buffer, index) =>
    ({
      index,
      domainType: LightingDomainType.Static,
      lightMode: buffer[0],
      // 协议亮度本身为 0-100，原样透出（UI 侧按 0-100 步进 25 提供 5 档）
      brightness: buffer[1],
      // GCOME_AP 速度档位语义与直觉相反（协议 0=最快，4=最慢），解析时反转对齐 UI“档位越大越快”
      speed: 4 - buffer[2],
      direction: buffer[3] === 1,
      isColorful: buffer[4] === 1,
      colorIndex: buffer[5],
      rgb: { r: buffer[6], g: buffer[7], b: buffer[8] },
      // GAME_MODE=0 自定义灯效为逐键单色（无渐变），gradientDirection=1
      gradientDirection: 1,
      buffer,
    }) as LightingConfigData & BacklightConfigExtra,
  serialize: (config) => {
    const buf = new Uint8Array(BACKLIGHT_SIZE)
    const extra = config as unknown as BacklightConfigExtra
    buf[0] = clamp(extra.lightMode ?? 0, 0, 0xff)
    // 协议亮度 0-100 原样写入（UI 端滑块按 step=25 量化为 0/25/50/75/100）
    buf[1] = clamp(config.brightness ?? 60, 0, 100)
    // 速度档位反转：UI 4(快) → 协议 0(快)；UI 0(慢) → 协议 4(慢)
    buf[2] = 4 - clamp(config.speed ?? 0, 0, 4)
    buf[3] = extra.direction ? 1 : 0
    buf[4] = config.isColorful ? 1 : 0
    buf[5] = extra.colorIndex ?? 0
    buf[6] = config.rgb?.r ?? 0
    buf[7] = config.rgb?.g ?? 0
    buf[8] = config.rgb?.b ?? 0
    return buf
  },
}

const backlightAddressResolver: LightingDomainDescriptor['addressResolver'] = {
  getBaseAddr: () => [BACKLIGHT_BASE],
  getFieldAddr: (field) => {
    const offsetMap: Record<string, number> = {
      brightness: 1,
      speed: 2,
      direction: 3,
      isColorful: 4,
      colorIndex: 5,
      r: 6,
      g: 7,
      b: 8,
    }
    return [BACKLIGHT_BASE + (offsetMap[field] ?? 0)]
  },
}

/** GCOME_AP 静态背光领域（逐键自定义灯效，isCustom 标注）。 */
export const staticBacklightDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Static,
  configSize: BACKLIGHT_SIZE,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.Speed |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Colorful |
    LightingFeatureFlag.KeyMap,
  addressResolver: backlightAddressResolver,
  parser: backlightParser,
  cacheTags: (index, configIndex) => [`light:static:${index}`, `cfg:${configIndex}`],
}

/** GCOME_AP 动态背光领域。 */
export const dynamicBacklightDomain: LightingDomainDescriptor = {
  type: LightingDomainType.Dynamic,
  configSize: BACKLIGHT_SIZE,
  supportedFeatures:
    LightingFeatureFlag.Brightness |
    LightingFeatureFlag.Speed |
    LightingFeatureFlag.SingleColor |
    LightingFeatureFlag.Colorful,
  addressResolver: backlightAddressResolver,
  parser: {
    parse: (buffer, index) => ({
      ...backlightParser.parse(buffer, index),
      domainType: LightingDomainType.Dynamic,
    }),
    serialize: backlightParser.serialize,
  },
  cacheTags: (_index, configIndex) => [`light:dynamic`, `cfg:${configIndex}`],
}
